import React, { useState, useCallback } from "react";
import { Form, Dropdown } from "react-bootstrap";
import { FaTrashAlt, FaPlus } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { RiEdit2Fill } from "react-icons/ri";
import { GoSidebarCollapse } from "react-icons/go";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import {
  BsDownload,
  BsFilter,
  BsInbox,
  BsSearch,
  BsSendFill,
  BsUpload,
} from "react-icons/bs";
import { TbRestore } from "react-icons/tb";

const IconButton = ({
  title,
  onClick,
  disabled = false,
  className = "icon-btn",
  children,
}) => (
  <button
    type="button"
    title={title}
    className={className}
    onClick={onClick}
    disabled={disabled}
  >
    {children}
  </button>
);

const ToolBar = ({
  sendNow,
  toggles,
  search,
  setSearch,
  checkedIds = [],
  setCheckedIds,
  showPreview,
  setShowPreview,
  setSelected,
  start = 0,
  end = 0,
  totalItems = 0,
  deleteData,
  restoreData,
  page = 1,
  setPage,
  totalPages = 1,
  addField,
  selectInboxType,
  editField,
  setPageSize,
  pageSize = 10,
  exportData,
  importData,
  setInboxType,
  inboxType = "all",
  addNewProduct,
  setAddNewProduct,
  updateNewProduct,
  setUpdateNewProduct,
}) => {
  const [filterDropdown, setFilterDropdown] = useState(false);

  const hasSelection = checkedIds.length > 0;
  const singleSelection = checkedIds.length === 1;

  const handleSearch = useCallback(
    (e) => {
      setSearch(e.target.value);
      setPage(1);
    },
    [setSearch, setPage],
  );

  const clearSelectionAndSearch = () => {
    setSearch("");
    setCheckedIds([]);
    setPage(1);
  };

  return (
    <div className="p-2 border-bottom bg-white">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
        {/* LEFT SECTION */}
        <div className="d-flex align-items-center gap-2">
          {addNewProduct && (
            <IconButton
              title="Add New"
              className="icon-btn primary"
              onClick={setAddNewProduct}
            >
              {sendNow ? <BsSendFill size={12} /> : <FaPlus size={12} />}
            </IconButton>
          )}

          {addField && (
            <IconButton
              title="Add Field"
              className="icon-btn primary"
              onClick={toggles?.setToggle}
            >
              {sendNow ? <BsSendFill size={12} /> : <FaPlus size={12} />}
            </IconButton>
          )}

          {selectInboxType && (
            <div className="select-with-icon">
              <span className="select-icon">
                <BsInbox />
              </span>
              <Form.Select
                size="sm"
                className="bg-light"
                value={inboxType}
                onChange={(e) => setInboxType(e.target.value)}
              >
                <option value="all">All</option>
                <option value="liveChat">Chats</option>
                <option value="tickets">Tickets</option>
                <option value="spam">Spam</option>
                <option value="deleted">Trash</option>
              </Form.Select>
            </div>
          )}

          {/* SEARCH */}
          <div className="position-relative">
            <BsSearch className="search-icon" />
            <Form.Control
              size="sm"
              placeholder="Search..."
              className="search-input"
              value={search}
              onChange={handleSearch}
            />
          </div>

          {/* FILTER */}
          <Dropdown
            align="end"
            show={filterDropdown}
            onToggle={setFilterDropdown}
          >
            <Dropdown.Toggle as="button" className="icon-btn" title="Filter">
              <BsFilter />
            </Dropdown.Toggle>

            <Dropdown.Menu className="shadow-sm" style={{ minWidth: 200 }}>
              {["Date Range", "Status", "Priority", "Agent", "Tags"].map(
                (item) => (
                  <Dropdown.Item key={item}>{item}</Dropdown.Item>
                ),
              )}
              <Dropdown.Divider />
              <Dropdown.Item
                className="text-danger"
                onClick={() => setFilterDropdown(false)}
              >
                Clear Filters
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {/* RIGHT SECTION */}
        <div className="d-flex align-items-center gap-3 text-muted small">
          {hasSelection && (
            <>
              <span className="fw-medium">{checkedIds.length} selected</span>

              {singleSelection && editField && !updateNewProduct && (
                <IconButton title="Edit" onClick={toggles?.setToggle1}>
                  <RiEdit2Fill />
                </IconButton>
              )}

              {singleSelection && updateNewProduct && (
                <IconButton
                  title="Edit"
                  onClick={() => setUpdateNewProduct(true)}
                >
                  <RiEdit2Fill />
                </IconButton>
              )}

              {deleteData && (
                <IconButton
                  title="Delete"
                  className="icon-btn danger"
                  onClick={deleteData}
                >
                  <FaTrashAlt />
                </IconButton>
              )}

              {restoreData && (
                <IconButton
                  title="Restore"
                  className="icon-btn success"
                  onClick={restoreData}
                >
                  <TbRestore />
                </IconButton>
              )}
            </>
          )}

          {(hasSelection || search) && (
            <IconButton
              title="Clear"
              className="icon-btn danger"
              onClick={clearSelectionAndSearch}
            >
              <FaXmark />
            </IconButton>
          )}

          {showPreview && (
            <IconButton
              title="Collapse Preview"
              onClick={() => {
                setShowPreview("");
                setSelected("");
              }}
            >
              <GoSidebarCollapse />
            </IconButton>
          )}

          {exportData && (
            <IconButton title="Export">
              <BsDownload />
            </IconButton>
          )}

          {importData && (
            <IconButton title="Import">
              <BsUpload />
            </IconButton>
          )}

          <span>
            {start}–{end} of {totalItems}
          </span>

          <Form.Select
            size="sm"
            style={{ width: 70 }}
            value={Number(pageSize)}
            onChange={(e) => setPageSize(Number(e.target.value))}
          >
            {[5, 10, 15, 25, 50, 100, 500].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Form.Select>

          <div className="d-flex gap-1">
            <IconButton
              title="Previous"
              disabled={page === 1}
              onClick={() => setPage((p) => p - 1)}
            >
              <IoIosArrowBack />
            </IconButton>

            <IconButton
              title="Next"
              disabled={page === totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              <IoIosArrowForward />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ToolBar;
